<template>
  <BaseBody>
    <BaseTopBar>
      <BaseTopBarContent className="left">
        {{ $t('sidebar.billinginvoice') }} > {{ data.code }}
      </BaseTopBarContent>
      <BaseTopBarContent className="right">
        <div class="action">
          <BaseButton
            v-if="data.status !== 'paid'"
            className="btn-info"
            @click="onClickPay($event, data)"
          >
            {{ $t('apps.billing.button.payamount', { number: getCurrencyWithNumber(numberel(data.total, '0,0.00')) }) }}
          </BaseButton>
          <div
            v-if="data.status === 'paid'"
            class="dropdown nodro item">
            <span class="text-success">
              {{ $t('apps.billing.status.paid') }}
            </span>
          </div>
        </div>
      </BaseTopBarContent>
    </BaseTopBar>
    <BaseContent :loading="loading">
      <BillingInvoiceSummary
        :data="data"
        :apps="apps"
      />
      <BillingInvoiceUsage
        v-model="tabSelected"
        :dateStart="dateStart"
        :dateEnd="dateEnd"
        :records="creditRecords"
        :files="creditFiles"
        :lineMessages="creditLineMessages"
        :emails="creditEmails"
        :qrcodes="creditQRCodes"
        :branches="creditBranches"
        :warehouses="creditWarehouses"
        :users="creditUsers"
        :apps="creditApps"
      />
    </BaseContent>
  </BaseBody>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { BILLING_SERVICE_USAGE, HAS_FEE_APP_MODULES } from '../../../../helpers/enum'
import sidebarModule from './../../sidebarModule'

export default {
  data () {
    return {
      loading: false,
      tabSelected: null,
      query: {
        id: this.$route.query.id,
        code: this.$route.query.code,
      },
    }
  },

  computed: {
    ...mapState('billing', {
      data: 'invoiceSingle',
      creditRecords: 'invoiceCreditRecordList',
      creditFiles: 'invoiceCreditFileList',
      creditLineMessages: 'invoiceCreditLineMessageList',
      creditEmails: 'invoiceCreditEmailList',
      creditQRCodes: 'invoiceCreditQRCodeList',
      creditBranches: 'invoiceCreditBranchList',
      creditWarehouses: 'invoiceCreditWarehouseList',
      creditUsers: 'invoiceCreditUserList',
      creditApps: 'invoiceCreditAppList',
    }),
    ...mapGetters({
      sidebar: 'getSidebar',
      getApps: 'getApps',
    }),
    currentTabPeriodDateTo () {
      let start = ''
      let end = ''
      if (this.tabSelected === BILLING_SERVICE_USAGE.RECORD) {
        start = this.data.appDateStart
        end = this.data.appDateEnd
      } else if (this.tabSelected === BILLING_SERVICE_USAGE.FILE) {
        start = this.data.fileDateStart
        end = this.data.fileDateEnd
      } else if (this.tabSelected === BILLING_SERVICE_USAGE.MESSAGE_LINE) {
        start = this.data.lineDateStart
        end = this.data.lineDateEnd
      } else if (this.tabSelected === BILLING_SERVICE_USAGE.EMAIL) {
        start = this.data.emailDateStart
        end = this.data.emailDateEnd
      } else if (this.tabSelected === BILLING_SERVICE_USAGE.QRCODE) {
        start = this.data.qrcodeDateStart
        end = this.data.qrcodeDateEnd
      } else if (this.tabSelected === BILLING_SERVICE_USAGE.BRANCH) {
        start = this.data.branchDateStart
        end = this.data.branchDateEnd
      } else if (this.tabSelected === BILLING_SERVICE_USAGE.WAREHOUSE) {
        start = this.data.warehouseDateStart
        end = this.data.warehouseDateEnd
      } else if (this.tabSelected === BILLING_SERVICE_USAGE.USER) {
        start = this.data.userDateStart
        end = this.data.userDateEnd
      } else if (`${this.tabSelected}`.search('APPID') >= 0) {
        const re = /APPID(\d+)_MODULEID(\d+)/g
        const match = re.exec(this.tabSelected)
        if (match != null) {
          const appId = Number(match[1])
          const moduleId = Number(match[2])
          const app = this.data.apps.find((app) => (
            app.appId === appId && app.moduleId === moduleId
          ))
          start = app.dateStart
          end = app.dateEnd
        }
      }
      return { start, end }
    },
    dateStart () {
      return this.currentTabPeriodDateTo.start
    },
    dateEnd () {
      return this.currentTabPeriodDateTo.end
    },
    apps () {
      return this.getApps.map((app) => {
        return {
          ...app,
          title: this.$t(`apps.billing.apps.appid${app.app_id}`),
          modules: app.modules.map((module) => {
            return {
              ...module,
              title: this.$t(`apps.billing.apps.appid${app.app_id}-moduleid${module.module_id}`),
            }
          }),
        }
      }).reduce((acc, cur) => {
        cur.modules.forEach(module => {
          const result = (() => {
            const result = this.data.apps.find((app) => (
              app.appId === cur.app_id &&
              app.moduleId === module.module_id
            ))
            if (result != null) {
              return {
                amountTotal: result.amountTotal,
                unitPrice: result.unitPrice,
              }
            }
            return null
          })()
          const hasFee = result != null
          if (hasFee) {
            acc.push({
              app: {
                id: cur.app_id,
                title: cur.title,
              },
              module: {
                id: module.module_id,
                title: module.title,
              },
              freeUsedTotal: 0,
              usedTotal: 1,
              chargeUsedTotal: 1,
              amountTotal: result.amountTotal,
              unitPrice: result.unitPrice,
            })
          }
        })
        return acc
      }, [])
    },
  },

  beforeMount () {
    this.initPage({
      section: 'app',
      name: this.$t('app.billing.title'),
      current: {
        app_name: 'billing',
        app_id: null,
      },
    })
    sidebarModule.init(this.getObject(require('../sidebar.json')))
    sidebarModule.update(this.sidebar, 1, [{
      'name': this.query.code,
      'router': {
        'name': 'BillingInvoiceSingle',
        'query': {
          id: this.query.id,
          code: this.query.code,
        },
      },
      'activeClass': 'active',
    }], {
      sidebarAtIdx: 1,
      submenuAtIdx: 1,
    })
  },

  async mounted () {
    await this.fetchData()
    this.setState()
  },

  destroyed () {
    this.leavePage(this.$Progress, [])
  },

  methods: {
    ...mapActions('billing', {
      getInvoiceSingle: 'getInvoiceSingle',
      getInvoiceCreditRecordList: 'getInvoiceCreditRecordList',
      getInvoiceCreditFileList: 'getInvoiceCreditFileList',
      getInvoiceCreditLineMessageList: 'getInvoiceCreditLineMessageList',
      getInvoiceCreditEmailList: 'getInvoiceCreditEmailList',
      getInvoiceCreditQRCodeList: 'getInvoiceCreditQRCodeList',
      getInvoiceCreditBranchList: 'getInvoiceCreditBranchList',
      getInvoiceCreditWarehouseList: 'getInvoiceCreditWarehouseList',
      getInvoiceCreditUserList: 'getInvoiceCreditUserList',
      getInvoiceCreditAppList: 'getInvoiceCreditAppList',
    }),
    async fetchData () {
      const { id } = this.query
      this.loading = true
      const getApps = HAS_FEE_APP_MODULES.map((app) => (
        this.getInvoiceCreditAppList({ id,
          params: {
            app_id: app.app_id,
            module_id: app.module_id,
          } })
      ))
      await Promise.all([
        this.getInvoiceSingle({ id }),
        this.getInvoiceCreditRecordList({ id }),
        this.getInvoiceCreditFileList({ id }),
        this.getInvoiceCreditLineMessageList({ id }),
        this.getInvoiceCreditEmailList({ id }),
        this.getInvoiceCreditQRCodeList({ id }),
        this.getInvoiceCreditBranchList({ id }),
        this.getInvoiceCreditWarehouseList({ id }),
        this.getInvoiceCreditUserList({ id }),
        ...getApps,
      ])
      this.loading = false
      this.showFinish(this.$Progress)
    },
    setState () {
      if (this.creditBranches.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.BRANCH
      } else if (this.creditWarehouses.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.WAREHOUSE
      } else if (this.creditUsers.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.USER
      } else if (this.creditRecords.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.RECORD
      } else if (this.creditFiles.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.FILE
      } else if (this.creditLineMessages.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.MESSAGE_LINE
      } else if (this.creditEmails.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.EMAIL
      } else if (this.creditQRCodes.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.QRCODE
      } else {
        this.tabSelected = null
      }

      if (this.tabSelected == null) {
        const hasDataInKey = Object.keys(this.creditApps).reduce((acc, cur) => {
          const key = cur
          if (this.creditApps[key].length && !acc) {
            acc = key
          }
          return acc
        }, null)
        this.tabSelected = hasDataInKey
      }
    },
    onClickPay (e, item) {
      this.$router.push({ name: 'PaymentInvoice',
        query: {
          id: item.invId,
        } })
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/apps/billing/invoice/Single.vue